import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import studentService from '../../services/studentService';
import adminService from '../../services/adminService';
import { generateStudentAchievementPDF } from '../../services/reportService';
import api from '../../api/api';
import { isToday, formatDate } from '../../utils/dateUtils';
import '../../styles/students/StudentProfilePage.css';
import '../../styles/students/StudentAssignmentCardFix.css';
import '../../styles/AssignmentsListPage.css';

const AdminStudentProfile = () => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [generatingReportType, setGeneratingReportType] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState('last30days');
  const [studentParents, setStudentParents] = useState([]);
  const [learningPlans, setLearningPlans] = useState([]);
  const [learningPlanLoading, setLearningPlanLoading] = useState(true);
  const [expandedPlans, setExpandedPlans] = useState(new Set());
  
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  
  // State for admin-calculated progress metrics
  const [dashboardStats, setDashboardStats] = useState({
    assignmentCompletion: 0,
    assessmentAverage: { value: 0, text: 'N/A' },
    attendanceRate: 0,
    // Additional details for display
    completedAssignments: 0,
    totalAssignments: 0,
    assessedAssignments: 0,
    presentDays: 0,
    totalDays: 0
  });

  // Calculate progress metrics (consistent with useStudentProgress hook logic)
  const calculateAdminProgressMetrics = (assignmentsData, attendanceData) => {
    if (!assignmentsData) assignmentsData = [];
    if (!attendanceData) attendanceData = [];
    
    // Calculate assignment completion rate
    const totalAssignments = assignmentsData.length;
    const completedAssignments = assignmentsData.filter(assignment => 
      assignment.status === 'completed' &&
      (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
    ).length;
    
    const completionRate = totalAssignments > 0 
      ? Math.round((completedAssignments / totalAssignments) * 100) 
      : 0;
    
    // Calculate assessment average
    const assessedAssignments = assignmentsData.filter(assignment => 
      assignment.assessment && assignment.assessment.rating
    );
    
    let averageValue = 0;
    let averageText = 'N/A';
    
    if (assessedAssignments.length > 0) {
      const ratingToNumber = {
        'Excellent': 5,
        'Very Good': 4,
        'Good': 3,
        'Pass': 2,
        'Need to Repeat': 1
      };
      
      const totalRating = assessedAssignments.reduce((sum, assignment) => {
        const numericRating = ratingToNumber[assignment.assessment.rating] || 0;
        return sum + numericRating;
      }, 0);
      
      averageValue = Math.round((totalRating / assessedAssignments.length) * 10) / 10;
      averageText = `${averageValue}/5`;
    }
    
    // Calculate attendance rate (counting both 'present' and 'late' as attendance)
    const totalAttendance = attendanceData.length;
    const presentCount = attendanceData.filter(record => 
      record.status === 'present' || record.status === 'late'
    ).length;
    const attendanceRate = totalAttendance > 0 
      ? Math.round((presentCount / totalAttendance) * 100) 
      : 0;
    
    return {
      assignmentCompletion: completionRate,
      assessmentAverage: { value: averageValue, text: averageText },
      attendanceRate: attendanceRate,
      // Additional details for display
      completedAssignments: completedAssignments,
      totalAssignments: totalAssignments,
      assessedAssignments: assessedAssignments.length,
      presentDays: presentCount,
      totalDays: totalAttendance
    };
  };

  const handleGeneratePDF = async (reportType = 'achievement') => {
    if (!id) {
      alert('No student selected for report generation');
      return;
    }

    setGeneratingReportType(reportType);
    try {
      const result = await generateStudentAchievementPDF(id, {
        dateRange: selectedDateRange,
        reportType: reportType // 'achievement' or 'progress'
      });
      
      console.log('PDF generated successfully:', result.filename);
      
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert(`Failed to generate report: ${error.message}`);
    } finally {
      setGeneratingReportType(null);
    }
  };

  useEffect(() => {
    if (!id) {
      console.error('No student ID provided');
      navigate('/reports');
      return;
    }

    console.log('Admin fetching complete data for student ID:', id);

    const fetchStudentData = async () => {
      try {
        console.log('=== ADMIN FETCHSTUDENTDATA STARTED ===');
        setLoading(true);
        
        // Initialize student object with basic structure
        let studentObj = {
          _id: id,
          name: 'Loading...',
          email: '',
          notes: '',
          classNames: []
        };
        
        try {
          // Get student data with populated class names using admin service
          console.log('Admin fetching student profile with class names for ID:', id);
          const studentData = await adminService.getStudentProfile(id);
          console.log('Admin student data received:', studentData);
          
          // Update student object with retrieved data (class names already populated by backend)
          studentObj = {
            _id: studentData._id || id,
            name: studentData.name || 'Error Loading Name',
            email: studentData.email || '',
            notes: studentData.notes || '',
            studentInfo: studentData.studentInfo || {},
            classNames: studentData.classNames || [] // Class names already populated by backend
          };

          console.log('Class names loaded:', studentObj.classNames);
          
        } catch (studentDataError) {
          console.error('Admin error fetching student data:', studentDataError);
        }

        // Fetch assignments and attendance using admin service
        try {
          console.log('Admin fetching assignments and attendance for progress calculation');
          const [assignmentsData, attendanceData] = await Promise.all([
            adminService.getStudentAssignments(id),
            adminService.getStudentAttendance(id)
          ]);

          console.log('Admin progress data received:', {
            assignments: assignmentsData.length,
            attendance: attendanceData.length
          });

          // Calculate progress metrics (consistent with hook logic)
          const calculatedStats = calculateAdminProgressMetrics(assignmentsData, attendanceData);
          setDashboardStats(calculatedStats);

        } catch (progressError) {
          console.error('Admin error fetching progress data:', progressError);
          // Keep default stats on error
        }
        
        // Set the final student data
        console.log('Admin setting final student data:', studentObj);
        setStudent(studentObj);
        setNotes(studentObj.notes || '');
        setLoading(false);
      } catch (error) {
        console.error('Admin fatal error in fetchStudentData:', error);
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [id, navigate]);

  // Fetch student parents
  useEffect(() => {
    const fetchStudentParents = async () => {
      if (!id) return;

      try {
        const response = await api.get(`/students/${id}/parents`);
        setStudentParents(response.data.data || []);
      } catch (error) {
        console.error('Error fetching student parents:', error);
      }
    };

    fetchStudentParents();
  }, [id]);

  // Fetch student's active learning plans
  useEffect(() => {
    const fetchLearningPlans = async () => {
      if (!id) return;

      try {
        setLearningPlanLoading(true);
        const response = await api.get(`/learning-plans/student/${id}`);
        const plans = response.data.data || [];

        // Filter for active plans only
        const activePlans = plans.filter(plan => plan.status === 'active');

        // Enrich each plan with average rating
        const enrichedPlans = await Promise.all(
          activePlans.map(async (plan) => {
            try {
              const assignmentsResponse = await api.get(`/learning-plans/${plan._id}/assignments`);
              const assignments = assignmentsResponse.data.data || [];

              // Calculate average rating (excluding "Need to Repeat")
              const getRatingValue = (rating) => {
                const ratingMap = {
                  'Excellent': 5,
                  'Very Good': 4,
                  'Good': 3,
                  'Pass': 2,
                  'Weak': 1
                };
                return ratingMap[rating] || 0;
              };

              const ratedAssignments = assignments.filter(a =>
                a.assessment &&
                a.assessment.rating &&
                a.assessment.rating !== 'Need to Repeat'
              );

              if (ratedAssignments.length > 0) {
                let totalRating = 0;
                ratedAssignments.forEach(assignment => {
                  totalRating += getRatingValue(assignment.assessment.rating);
                });

                plan.averageRating = (totalRating / ratedAssignments.length).toFixed(1);
                plan.ratedAssignmentsCount = ratedAssignments.length;
              }

              return plan;
            } catch (assignmentsError) {
              console.error('Error fetching plan assignments for rating:', assignmentsError);
              // Return plan without average rating
              return plan;
            }
          })
        );

        setLearningPlans(enrichedPlans);

        // Set default expanded state: expand all if only 1 plan, collapse all if 2+
        if (enrichedPlans.length === 1) {
          setExpandedPlans(new Set([enrichedPlans[0]._id]));
        } else {
          setExpandedPlans(new Set());
        }
      } catch (error) {
        console.error('Error fetching learning plans:', error);
        setLearningPlans([]);
      } finally {
        setLearningPlanLoading(false);
      }
    };

    fetchLearningPlans();
  }, [id]);

  const togglePlanExpansion = (planId) => {
    setExpandedPlans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading student profile...</p>
      </div>
    );
  }

  return (
    <div className="student-profile-page">
      <div className="page-header">
        <h1>Student Profile (Admin View)</h1>
        <div className="header-actions">
          <button 
            onClick={() => navigate('/reports')} 
            className="back-button"
          >
            ← Back to Reports
          </button>
          <Link to="/dashboard" className="dashboard-link">
            Dashboard
          </Link>
        </div>
      </div>

      {/* Student Profile Card */}
      <div className="profile-card">
        <div className="profile-avatar">
          {student.name?.charAt(0) || '?'}
        </div>
        <div className="profile-info">
          <h2 className="profile-name">{student.name}</h2>
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            marginTop: '4px',
            padding: '4px 8px',
            backgroundColor: '#fff3cd',
            borderRadius: '4px',
            border: '1px solid #ffeaa7'
          }}>
            Admin View - Complete cross-class data
          </div>
          {student.classNames && student.classNames.length > 0 && (
            <div style={{ 
              fontSize: '14px', 
              color: '#333', 
              marginTop: '8px',
              padding: '4px 8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #dee2e6'
            }}>
              Classes: {student.classNames.join(', ')}
            </div>
          )}
        </div>
      </div>

      {/* Parent Information Section */}
      {studentParents.length > 0 && (
        <div className="parent-info-section" style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <h3 style={{ 
            margin: '0 0 15px 0', 
            fontSize: '18px', 
            color: '#333',
            borderBottom: '2px solid #4a90e2',
            paddingBottom: '8px'
          }}>
            Parent/Guardian Information ({studentParents.length})
          </h3>
          <div className="parents-grid" style={{
            display: 'grid',
            gridTemplateColumns: studentParents.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '15px'
          }}>
            {studentParents.map((parent, index) => (
              <div key={parent._id} className="parent-info-card" style={{
                padding: '15px',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                backgroundColor: '#f9f9f9'
              }}>
                <div className="parent-header" style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <div className="parent-avatar" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#28a745',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    marginRight: '12px'
                  }}>
                    {parent.name?.charAt(0) || 'P'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333' }}>
                      {parent.name}
                    </div>
                    {studentParents.length > 1 && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {index === 0 ? 'Primary Parent' : 'Guardian'}
                      </div>
                    )}
                  </div>
                </div>
                <div className="parent-contact" style={{ fontSize: '14px', color: '#666' }}>
                  {parent.email && (
                    <div style={{ marginBottom: '4px' }}>
                      📧 <a href={`mailto:${parent.email}`} style={{ color: '#4a90e2', textDecoration: 'none' }}>
                        {parent.email}
                      </a>
                    </div>
                  )}
                  {parent.phone && (
                    <div>
                      📞 <a href={`tel:${parent.phone}`} style={{ color: '#4a90e2', textDecoration: 'none' }}>
                        {parent.phone}
                      </a>
                    </div>
                  )}
                  {/* Admin-specific: Show parent ID for troubleshooting */}
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '8px', fontFamily: 'monospace' }}>
                    Parent ID: {parent._id}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Summary Section */}
      <h2 className="section-title">Progress Summary (All Classes)</h2>
      <div className="stats-container" style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px 10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        margin: '0 0 20px 0',
        overflow: 'hidden'
      }}>
        <div className="stat-item" style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.assignmentCompletion}%</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666' }}>Assignments</div>
          <div className="stat-detail" style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            {dashboardStats.completedAssignments} of {dashboardStats.totalAssignments} completed
          </div>
          <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', width: '1px', height: '60%', backgroundColor: '#e0e0e0' }}></div>
        </div>
        
        <div className="stat-item" style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.assessmentAverage.value}/5</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666' }}>Avg. Rating</div>
          <div className="stat-detail" style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            Based on {dashboardStats.assessedAssignments} assessed assignments
          </div>
          <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', width: '1px', height: '60%', backgroundColor: '#e0e0e0' }}></div>
        </div>
        
        <div className="stat-item" style={{ flex: 1, textAlign: 'center' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.attendanceRate}%</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>Attendance</div>
          <div className="stat-detail" style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            Present: {dashboardStats.presentDays}/{dashboardStats.totalDays} days
          </div>
        </div>
      </div>

      {/* Learning Plans Section */}
      {!learningPlanLoading && (
        <>
          <h2 className="section-title">Learning Plans {learningPlans.length > 0 && `(${learningPlans.length})`}</h2>
          {learningPlans.length > 0 ? (
            learningPlans.map((learningPlan) => {
              const isExpanded = expandedPlans.has(learningPlan._id);
              const overdue = learningPlan.progress?.due || 0;
              const scheduleStatus = overdue === 0
                ? { text: 'On Track', icon: '✓', color: '#4caf50' }
                : overdue >= 3
                ? { text: 'Behind Schedule', icon: '⚠', color: '#f44336' }
                : { text: 'Slightly Behind', icon: '⚠', color: '#ff9800' };

              return (
            <div key={learningPlan._id} style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              border: '1px solid #e0e0e0'
            }}>
              {/* Clickable Header for Expand/Collapse */}
              <div
                onClick={() => togglePlanExpansion(learningPlan._id)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: isExpanded ? '15px' : '12px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: '0', fontSize: '18px', color: '#333' }}>
                      {learningPlan.goal.type === 'pages' && 'Pages Goal'}
                      {learningPlan.goal.type === 'verses' && 'Verses Goal'}
                      {learningPlan.goal.type === 'lines' && 'Lines Goal'}
                      {learningPlan.goal.type === 'juz' && `Juz ${learningPlan.goal.juz?.number}`}
                      {learningPlan.goal.type === 'surahs' && 'Surahs Goal'}
                    </h3>

                    {/* Assignment Type Badge */}
                    <span style={{
                      padding: '4px 10px',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {learningPlan.assignmentType ? learningPlan.assignmentType.replace('-', ' ') : 'New Memorization'}
                    </span>

                    {/* Average Rating */}
                    {learningPlan.averageRating && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '12px'
                        }}
                        title={`Based on ${learningPlan.ratedAssignmentsCount || 0} assessed assignment${learningPlan.ratedAssignmentsCount !== 1 ? 's' : ''}`}
                      >
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
                          {learningPlan.averageRating}/5
                        </span>
                        <span style={{ fontSize: '11px', color: '#666' }}>
                          Avg
                        </span>
                      </div>
                    )}

                    {/* Progress % */}
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#4a90e2' }}>
                      {learningPlan.progress?.percent || 0}%
                    </span>

                    {/* Schedule Status Chip */}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      backgroundColor: scheduleStatus.color === '#4caf50' ? '#e8f5e9' : scheduleStatus.color === '#ff9800' ? '#fff3e0' : '#ffebee',
                      borderRadius: '12px'
                    }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: scheduleStatus.color }}>
                        {scheduleStatus.icon} {scheduleStatus.text}
                      </span>
                    </div>
                  </div>

                  {/* Goal Summary */}
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    {learningPlan.goal.type === 'pages' && `Pages ${learningPlan.goal.pages?.from}-${learningPlan.goal.pages?.to}`}
                    {learningPlan.goal.type === 'verses' && `${learningPlan.goal.verses?.total || 0} verses`}
                    {learningPlan.goal.type === 'lines' && `~${learningPlan.goal.lines?.dailyLines || 0} lines per day`}
                    {learningPlan.goal.type === 'juz' && `${learningPlan.goal.juz?.versesCount || 0} verses`}
                    {learningPlan.goal.type === 'surahs' && `${learningPlan.goal.surahs?.length || 0} surahs`}
                    {learningPlan.goal.type === 'verses' && learningPlan.goal.verses?.startFrom && learningPlan.goal.verses?.endAt && (
                      <span style={{ color: '#999', fontSize: '13px', marginLeft: '8px' }}>
                        ({learningPlan.goal.verses.startFrom.surahName || `Surah ${learningPlan.goal.verses.startFrom.surah}`} {learningPlan.goal.verses.startFrom.verse} → {learningPlan.goal.verses.endAt.surahName || `Surah ${learningPlan.goal.verses.endAt.surah}`} {learningPlan.goal.verses.endAt.verse})
                      </span>
                    )}
                    {learningPlan.goal.type === 'lines' && learningPlan.goal.lines?.startFrom && (
                      <span style={{ color: '#999', fontSize: '13px', marginLeft: '8px' }}>
                        (Starting from {learningPlan.goal.lines.startFrom.surahName || `Surah ${learningPlan.goal.lines.startFrom.surah}`}, Verse {learningPlan.goal.lines.startFrom.verse})
                      </span>
                    )}
                  </div>
                </div>

                {/* Expand/Collapse Icon */}
                <div style={{
                  fontSize: '24px',
                  color: '#666',
                  marginLeft: '12px',
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                  ▼
                </div>
              </div>

              {/* View Full Plan Button - Always Visible */}
              <div style={{ marginBottom: isExpanded ? '15px' : '0' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering collapse/expand
                    navigate(`/learning-plans/${learningPlan._id}`);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  View Full Plan →
                </button>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
              <>

              {/* Progress Bar */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Progress</span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#4a90e2' }}>
                    {learningPlan.progress?.percent || 0}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${learningPlan.progress?.percent || 0}%`,
                    height: '100%',
                    backgroundColor: learningPlan.progress?.percent >= 75 ? '#4caf50' : learningPlan.progress?.percent >= 50 ? '#2196f3' : learningPlan.progress?.percent >= 25 ? '#ff9800' : '#f44336',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', fontSize: '12px', color: '#888' }}>
                  <span>{learningPlan.progress?.completed || 0} of {learningPlan.progress?.total || 0} assignments completed</span>
                  <span>{(learningPlan.progress?.total || 0) - (learningPlan.progress?.completed || 0)} remaining</span>
                </div>

                {/* Overdue Details (if applicable) */}
                {overdue > 0 && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px 10px',
                    backgroundColor: '#fff3e0',
                    borderLeft: `3px solid ${scheduleStatus.color}`,
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#666'
                  }}>
                    {overdue} overdue assignment{overdue !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Schedule Info */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>Schedule</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {learningPlan.schedule.daysPerWeek} days/week
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>Days</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {learningPlan.schedule.specificDays.map(d => d.substring(0, 3)).join(', ')}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>End Date</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {formatDate(learningPlan.timeline.end)}
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
              );
            })
          ) : (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px 20px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              border: '1px solid #e0e0e0',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                No active learning plans
              </p>
            </div>
          )}
        </>
      )}

      {/* Performance Links Section */}
      <h2 className="section-title">Performance (All Classes)</h2>
      <div className="performance-container">
        <Link 
          to={`/admin/students/${student._id || id}/assignments`}
          className="performance-link"
          state={{ 
            studentId: student._id || id, 
            studentName: student.name
          }}
        >
          <h3 className="performance-link-title">All Assignments</h3>
          <p className="performance-link-subtitle">
            View all assignments across all classes
          </p>
        </Link>
        
        <div className="performance-divider"></div>
        
        <Link 
          to={`/admin/students/${student._id || id}/attendance`}
          className="performance-link"
          state={{ 
            studentId: student._id || id, 
            studentName: student.name
          }}
        >
          <h3 className="performance-link-title">All Attendance</h3>
          <p className="performance-link-subtitle">
            View attendance history across all classes
          </p>
        </Link>
        
        <div className="performance-divider"></div>
        
        {/* Report Generation Section */}
        <div className="performance-link">
          <h3 className="performance-link-title">Student Reports</h3>
          <p className="performance-link-subtitle">
            Generate PDF reports for sharing and records
          </p>
          
          <div className="report-section" style={{ marginTop: '15px' }}>
            <div className="report-controls">
              <div className="report-period">
                <label className="report-label">Report Period:</label>
                <select 
                  className="date-range-select"
                  value={selectedDateRange}
                  onChange={(e) => setSelectedDateRange(e.target.value)}
                >
                  <option value="last7days">Last 7 days</option>
                  <option value="last30days">Last 30 days</option>
                  <option value="last60days">Last 60 days</option>
                  <option value="last90days">Last 90 days</option>
                  <option value="month">Current Month</option>
                </select>
              </div>
              <div className="report-buttons">
                <button 
                  className={`achievement-report-btn ${generatingReportType === 'achievement' ? 'generating' : ''}`}
                  onClick={() => handleGeneratePDF('achievement')}
                  disabled={generatingReportType !== null}
                >
                  {generatingReportType === 'achievement' ? 'Generating...' : '🌟 Generate Achievement Report'}
                </button>
                <button 
                  className={`progress-report-btn ${generatingReportType === 'progress' ? 'generating' : ''}`}
                  onClick={() => handleGeneratePDF('progress')}
                  disabled={generatingReportType !== null}
                >
                  {generatingReportType === 'progress' ? 'Generating...' : '📊 Generate Progress Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section - Read Only for Admin */}
      <h2 className="section-title">Notes (Read Only)</h2>
      <div className="notes-container">
        <textarea
          className="notes-input"
          placeholder="Student notes (managed by class teachers)..."
          value={notes}
          readOnly
          style={{
            backgroundColor: '#f8f9fa',
            cursor: 'not-allowed',
            resize: 'none'
          }}
        ></textarea>
        <div style={{ 
          fontSize: '12px', 
          color: '#666', 
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          Note: Admin view is read-only. Notes are managed by class teachers.
        </div>
      </div>
    </div>
  );
};

export default AdminStudentProfile;